import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Calendar, ChevronRight, Shirt, Clock, Trash2, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, Badge, Thumb, Modal, LoadingState, ErrorState } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import api from '@/lib/api';
import { STATUS_LABELS } from '@/lib/constants';
import { formatDate } from '@/lib/utils';
import type { FitPiece } from '@/lib/types';

type DiffStatus = 'kept' | 'removed' | 'current' | 'added';

interface VersionPiece extends FitPiece {
  diffStatus?: DiffStatus;
}

interface VersionResponse {
  version: {
    versionNumber: number;
    note: string | null;
    createdAt: string;
    isCurrent: boolean;
    pieces: VersionPiece[];
  };
}

const DIFF_TONE: Record<DiffStatus, 'success' | 'danger' | 'accent' | 'neutral'> = {
  kept: 'neutral',
  current: 'success',
  removed: 'danger',
  added: 'accent',
};

const DIFF_LABEL: Record<DiffStatus, string> = {
  kept: 'Kept',
  current: 'Current',
  removed: 'Removed',
  added: 'Added',
};

export function FitVersionPage() {
  const { id, version } = useParams<{ id: string; version: string }>();
  const navigate = useNavigate();
  const { data, loading, error } = useApi<VersionResponse>(
    id && version ? `/fits/${id}/versions/${version}` : null,
  );

  const [showDelete, setShowDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!id || !version) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/fits/${id}/versions/${version}`);
      navigate(`/fits/${id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete version.';
      // 409: has wears or only version.
      setDeleteError(
        /409/.test(msg) || /wear/i.test(msg)
          ? 'This version cannot be deleted because it has logged wears or is the only version.'
          : msg,
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Page>
      <PageHeader section={`Version ${version ?? ''}`} backHref={`/fits/${id}`} backLabel="Back to Fit" />
      <Main className="space-y-4">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {data && (
          <>
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    data.version.isCurrent ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
                  }`}
                >
                  v{data.version.versionNumber}
                </span>
                {data.version.isCurrent && <span className="text-xs text-green-600 font-medium">Current</span>}
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <Calendar className="w-3 h-3" />
                Created {formatDate(data.version.createdAt)}
              </p>
              {data.version.note && (
                <div className="mt-3 p-3 bg-secondary/50 rounded-xl">
                  <p className="text-sm text-muted-foreground italic">{data.version.note}</p>
                </div>
              )}
            </Card>

            <div className="grid grid-cols-2 gap-3">
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Shirt className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Items</span>
                </div>
                <p className="text-2xl font-bold text-foreground">{data.version.pieces.length}</p>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Status</span>
                </div>
                <p className="text-sm font-medium text-foreground mt-1.5">
                  {data.version.isCurrent ? 'Current version' : 'Older version'}
                </p>
              </Card>
            </div>

            <Card className="overflow-hidden">
              <div className="p-4 border-b border-border">
                <h2 className="text-sm font-semibold text-foreground">Items in this Version</h2>
              </div>
              <div className="divide-y divide-border">
                {data.version.pieces.map((piece) => (
                  <div key={piece.id} className="p-4 flex items-start gap-3">
                    <Thumb url={piece.primaryPhotoUrl} alt={piece.name} className="w-12 h-12 rounded-lg flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/wardrobe/item/${piece.itemId}`}
                        className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {piece.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {piece.brand ? `${piece.brand} · ` : ''}
                        {piece.category}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        {piece.diffStatus && (
                          <Badge tone={DIFF_TONE[piece.diffStatus]}>{DIFF_LABEL[piece.diffStatus]}</Badge>
                        )}
                        <span className="text-[10px] text-muted-foreground">
                          {STATUS_LABELS[piece.status] ?? piece.status}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground mt-1" />
                  </div>
                ))}
              </div>
            </Card>

            {deleteError && <ErrorState message={deleteError} />}

            <Button variant="outline" fullWidth onClick={() => setShowDelete(true)}>
              <Trash2 className="w-4 h-4" />
              Delete this Version
            </Button>
          </>
        )}
      </Main>

      <Modal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete Version"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowDelete(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" fullWidth onClick={handleDelete} disabled={deleting}>
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-3 bg-destructive/10 rounded-xl p-3">
          <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-foreground">
            Delete version {version}? Versions with logged wears or the only version cannot be deleted.
          </p>
        </div>
      </Modal>
    </Page>
  );
}
